import {TreeOperationType} from '../../../shared/dtos/estimate-tree-operation.dto';
import {WorkItemType} from '../../../shared/dtos/estimate-tree.dto';

/*
 * The operations a work item offers and the questions each asks, as Android
 * has them (TreeOperationTypes.kt, OperationSchema.kt). Keys and stored values
 * are the wire's; what an option reads as can differ (Debris Removal's options
 * drop "Removal", Thinning's middle band reads "Standard").
 */

const T = TreeOperationType;

/** Removal first, then pruning, then specialty (design, 2026-07-23). */
const TREE_OPERATIONS: string[] = [
  T.Tree_Removal, T.Thinning, T.Custom_Trim_Prune, T.Ground_To_Canopy_Clearance, T.Crown_Reduction, T.Cabling, T.Tree_Health_Care,
];

export const operationsFor = (type: WorkItemType): string[] => {
  switch (type) {
    case WorkItemType.Tree:
    case WorkItemType.TreeGroup: return TREE_OPERATIONS;
    case WorkItemType.FallenTree: return [T.Tree_Removal];
    case WorkItemType.Stump: return [T.Stump_Grinding, T.Whole_Stump_Removal];
    case WorkItemType.DebrisPile: return [T.Debris_Removal];
    case WorkItemType.Misc: return [T.Misc_Task];
    default: return [];
  }
};

/** The names on screen; the wire values stay canonical. */
export const operationLabel = (type: string) => {
  switch (type) {
    case T.Ground_To_Canopy_Clearance: return 'Canopy Clearance';
    case T.Cabling: return 'Cabling/Bracing';
    case T.Misc_Task: return 'Misc. Task';
    default: return type;
  }
};

/** Operations whose content is a list of selections close it off with a rule before the note. */
export const hasSelectionList = (type: string) =>
  ([T.Thinning, T.Ground_To_Canopy_Clearance, T.Crown_Reduction, T.Debris_Removal] as string[]).includes(type);

export const WORK_ITEM_LABEL: Record<string, string> = {
  Tree: 'Tree', TreeGroup: 'Tree Group', FallenTree: 'Fallen Tree', Stump: 'Stump', DebrisPile: 'Debris Pile', Misc: 'Misc',
};

/** The add menu's names under each circle. */
export const ADD_MENU_LABEL: Record<string, string> = {...WORK_ITEM_LABEL, Misc: 'Misc.'};

/** "Tree Total"; Fallen Tree falls through to the plain "Tree Total". */
export const totalLabel = (type: string) => {
  switch (type) {
    case WorkItemType.Tree:
    case WorkItemType.FallenTree: return 'Tree Total';
    case WorkItemType.Misc: return 'Misc. Total';
    default: return `${WORK_ITEM_LABEL[type] ?? type} Total`;
  }
};

export const deleteWholeLabel = (type: string) => type === WorkItemType.Misc ? 'Misc.' : (WORK_ITEM_LABEL[type] ?? type);

// ---- The questions -----------------------------------------------------------------------------

export interface OperationField {
  name: string;
  label: string;
  kind: 'pick' | 'select' | 'text';
  options?: string[];
  /** What an option reads as where it differs from what is stored. */
  optionLabels?: Record<string, string>;
  /** A number on the wire ("12 in" picks store 12). */
  numeric?: boolean;
  /** Start empty instead of on the first option. */
  noDefault?: boolean;
}

export const operationFields = (type: string): OperationField[] => {
  switch (type) {
    case T.Thinning:
      return [
        {name: 'thinningAmount', label: 'Thinning Amount', kind: 'pick', options: ['Extra Light', 'Light', 'Standard', 'Heavy', 'Extra Heavy'], noDefault: true},
        {name: 'materialType', label: 'Branch Type', kind: 'pick', options: ['Live & Dead', 'Dead Only'], noDefault: true},
        {name: 'deadwoodAmount', label: 'Deadwood Amount', kind: 'pick', options: ['All visible', 'Most', 'Major limbs only', 'Minimal'], noDefault: true},
      ];
    case T.Stump_Grinding:
      return [
        {name: 'grindingDepthInches', label: 'Grinding Depth', kind: 'select', numeric: true,
          options: Array.from({length: 20}, (_, i) => `${i + 1} in`), noDefault: true},
        {name: 'grindingRadius', label: 'Grind Beyond Stump Edge', kind: 'select',
          options: ['Stump Only', 'All Surface Roots', ...Array.from({length: 19}, (_, i) => `${i * 0.5 + 1} ft`)], noDefault: true},
      ];
    case T.Custom_Trim_Prune: return [{name: 'notes', label: 'Custom Trim/Prune Details', kind: 'text'}];
    case T.Cabling: return [{name: 'notes', label: 'Installation Details', kind: 'text'}];
    case T.Debris_Management: return [{name: 'notes', label: 'Notes', kind: 'text'}];
    case T.Debris_Removal:
      return [{
        name: 'debrisType', label: 'Debris Type', kind: 'pick', noDefault: true,
        options: ['Brush Removal', 'Brush and Log Removal', 'Log Removal', 'Misc Debris Removal'],
        optionLabels: {'Brush Removal': 'Brush', 'Brush and Log Removal': 'Brush and Logs', 'Log Removal': 'Logs', 'Misc Debris Removal': 'Misc. Debris'},
      }];
    case T.Misc_Task: return [{name: 'descriptiveNote', label: 'Task Details', kind: 'text'}];
    case T.Tree_Health_Care: return [{name: 'treatmentDetails', label: 'Treatment Details', kind: 'text'}];
    default: return [];
  }
};

/** A fresh operation's details: options[0] unless it starts empty, "" for text (OperationSchema.defaultDetails). */
export const defaultDetails = (type: string): Record<string, string | number> => Object.fromEntries(
  operationFields(type)
    .filter(f => f.kind === 'text' || (f.options && !f.noDefault))
    .map(f => [f.name, f.kind === 'text' ? '' : f.options![0]]),
);

/** A stored answer as this app names it today (a stray "Medium" Thinning reads "Standard"). */
export const storedAnswer = (name: string, value: unknown): string | undefined => {
  if (value == null || value === '') return undefined;
  const text = String(value);
  return name === 'thinningAmount' && text === 'Medium' ? 'Standard' : text;
};

/**
 * The standard stump-grinding release of liability, seeded once as a new
 * Stump Grinding operation's note, two blank lines down so the estimator can
 * type above it.
 */
export const stumpGrindingSeedNote = (companyName: string) =>
  `\n\n${companyName} will not be liable for any damage to underground utilities or sprinkler pipes that may occur during the stump removal process`;

// ---- Measurements (WorkItemsView.kt) ------------------------------------------------------------

const measurementOptions = (unit: string) => {
  const out: string[] = [];
  for (let v = 0; v <= 500;) {
    out.push(`${v} ${unit}`);
    v += v < 50 ? 1 : v < 100 ? 2.5 : 5;
  }
  return out;
};

export const FEET_OPTIONS = measurementOptions('Feet');
export const INCH_OPTIONS = measurementOptions('Inches');
export const COUNT_OPTIONS = Array.from({length: 20}, (_, i) => String(i + 1));
/** The group's tree count keeps the old "<n> " wire value, trailing space and all. */
export const TREE_COUNT_OPTIONS = Array.from({length: 99}, (_, i) => `${i + 1} `);
